import { getAuth } from "firebase/auth";
import { app } from "./init";
import { getFunctions, httpsCallableFromURL, Functions } from "firebase/functions";

// 初始化 Functions
const functions: Functions = getFunctions(app, "asia-east1");

/**
 * STS Token 回應格式
 */
export interface BytePlusUploadTokenResponse {
  stsToken: {
    CurrentTime: string;
    ExpiredTime: string;
    SessionToken: string;
    AccessKeyID: string;
    SecretAccessKey: string;
  };
  appId: number;
  region: string;
  spaceName: string;
}

/**
 * 用 vid 從 BytePlus 取得媒體 metadata（width / height / duration / cover_url 等）
 * 用途：admin 在 BytePlus 後台直傳影片後，貼 vid 進 CMS 一鍵帶出欄位。
 */
export interface BytePlusTranscodeEntry {
  definition: string;
  width: number;
  height: number;
  codec: string;
  bitrate: number;
  format: string;
}

export interface BytePlusVideoMetadata {
  vid: string;
  title: string;
  poster_uri: string;
  cover_url: string;
  width: number;
  height: number;
  duration: number;
  bitrate: number;
  fps: number;
  format: string;
  file_type: string;
  space_name: string;
  create_time: string;
  publish_status: string;
  transcodes: BytePlusTranscodeEntry[];
  validation: {
    ok: boolean;
    warnings: string[];
  };
}

export async function getVideoMetadataByVid(
  vid: string,
  expectedOrientation?: number
): Promise<BytePlusVideoMetadata> {
  const auth = getAuth(app);
  const user = auth.currentUser;
  if (!user) {
    throw new Error("用戶未登入");
  }
  const idToken = await user.getIdToken();
  const projectId =
    app.options.projectId ||
    import.meta.env.VITE_FIREBASE_PROJECT_ID ||
    "onreel-dev";
  const functionUrl = `https://asia-east1-${projectId}.cloudfunctions.net/getVideoMetadataByVid`;

  const response = await fetch(functionUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({
      vid,
      ...(expectedOrientation === 0 || expectedOrientation === 1 ?
        {expected_orientation: expectedOrientation} : {}),
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error || `HTTP ${response.status}: ${response.statusText}`
    );
  }
  return response.json();
}

/**
 * 獲取 BytePlus 上傳 Token
 * 注意：這是一個 HTTP endpoint，需要使用 fetch 而不是 callable
 */
export async function getBytePlusUploadToken(
  dramaId: string,
  fileName?: string
): Promise<BytePlusUploadTokenResponse> {
  try {
    const auth = getAuth(app);
    const user = auth.currentUser;
    
    if (!user) {
      throw new Error("用戶未登入");
    }

    // 獲取 ID token
    const idToken = await user.getIdToken();

    // 構建 Functions URL
    // 優先使用 app 的 projectId，然後使用環境變數，最後使用 fallback
    const projectId = app.options.projectId || 
                      import.meta.env.VITE_FIREBASE_PROJECT_ID || 
                      "onreel-dev";
    const functionUrl = `https://asia-east1-${projectId}.cloudfunctions.net/getBytePlusUploadToken`;

    // 發送 HTTP 請求
    const response = await fetch(functionUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({ dramaId, fileName }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error: any) {
    console.error("Error getting BytePlus upload token:", error);
    throw new Error(error.message || "獲取上傳 Token 失敗");
  }
}


